// ─────────────────────────────────────────────────────────────────────────
// NEW PRICING-PAGE MODEL — "Implement new pricing page" ticket.
//
// The marketing /pricing model per the ticket: a per-plan CREDIT DROPDOWN (the
// same credit ladder on every paid plan) where the recurring price is
//   price = baseRatePerCredit × planMultiplier × credits
// with a monthly/annual toggle (annual = 2 months free = pay for 10 months).
//
// This is the ticket's single source of truth for the pricing page. It is meant
// to SUPERSEDE the standalone "rated" slider prototype (lib/billing/credit-rates
// .ts + components/billing/rated/*): the pricing page renders from THIS config;
// retiring the old slider + wiring checkout to these tiers + the plan-key
// rename/DB migration are the follow-up billing-internals step (kept separate
// because they touch live Stripe subscriptions + existing customer rows).
//
// NUMBERS — PANDA-1414 ("3× base credit cost"), confirmed by Aaron (Product):
// ONE flat rate of $0.097/credit on EVERY paid plan (the old ×2/×4 tier
// multipliers are gone), with a PER-PLAN base credit allowance replacing the
// old shared 650:
//
//   Pro    300 credits   → $29/mo
//   Grow   1,000 credits → $97/mo
//   Scale  3,000 credits → $291/mo
//
// Those three prices are only reachable by rounding the dollar total to the
// NEAREST dollar (300 × 9.7¢ = $29.10 → $29). The previous model rounded UP,
// which would price Pro at $30. See tierMonthlyCents.
// ─────────────────────────────────────────────────────────────────────────

export type PricingTierId = "free" | "pro" | "grow" | "scale";
export type PaidTierId = Exclude<PricingTierId, "free">;
export type BillingCadence = "monthly" | "annual";

export const PAID_TIER_IDS: readonly PaidTierId[] = ["pro", "grow", "scale"];

// Recurring cost per monthly credit, in CENTS — $0.097/credit (9.7¢), the SAME
// on every paid plan (PANDA-1414: 3× the old 3.3¢ base). Tiers no longer differ
// by rate; they differ by how many credits are INCLUDED (PLAN_BASE_CREDITS).
export const BASE_RATE_CENTS_PER_CREDIT = 9.7;

// Per-plan price multiplier. PANDA-1414 flattened the ladder to a single rate,
// so every tier is ×1. The symbol is retained (rather than deleted) because
// tierMonthlyCents and the PricingTier cards still read it, and keeping it at 1
// leaves one obvious lever if per-tier rates ever return. Do NOT reintroduce
// ×2/×4 here without also updating RATED_TIER_MULTIPLIER (lib/billing/credit-rates.ts)
// and topupPlanMultiplier (lib/plans.ts) — three copies of the same idea.
export const TIER_MULTIPLIER: Record<PaidTierId, number> = {
  pro: 1,
  grow: 1,
  scale: 1,
};

// Whether the tier's price is Product/Finance-approved to charge real money.
// Gate go-live (and the "placeholder" badge on the card) on these.
export const RATES_CONFIRMED: Record<PaidTierId, boolean> = {
  pro: true,
  grow: true,
  scale: true,
};

// The credits INCLUDED in each plan at its base price — the first (and cheapest)
// rung of that plan's dropdown. A subscriber can never go BELOW their plan's
// base, so this doubles as the validation floor. Replaces the old shared
// DEFAULT_LADDER_CREDITS = 650, which priced every tier at the same credit count
// and relied on the (now removed) multiplier to tell the plans apart.
export const PLAN_BASE_CREDITS: Record<PaidTierId, number> = {
  pro: 300,
  grow: 1000,
  scale: 3000,
};

// Monthly-credit ladder shown in each plan's dropdown. PER-PLAN now: the first
// entry is always that plan's base (you cannot buy fewer credits than your plan
// includes), and every higher rung is shared. Values confirmed by Product.
export const PLAN_CREDIT_LADDER: Record<PaidTierId, readonly number[]> = {
  pro: [300, 600, 800, 1000, 1200, 2000, 3000, 4000, 5000, 7500, 10000],
  grow: [1000, 1200, 2000, 3000, 4000, 5000, 7500, 10000],
  scale: [3000, 4000, 5000, 7500, 10000],
};

// Annual = 2 months free → customer pays for 10 months of the monthly rate.
export const ANNUAL_MONTHS_CHARGED = 10;

// ── price math (all return whole CENTS; never trust a client-supplied price) ──

/**
 * Recurring monthly price for a tier + chosen monthly-credit total.
 *
 * The dollar total is rounded to the NEAREST whole dollar, so the shown price
 * always equals the charged price (no fractional-dollar invoices), e.g.
 *   300cr × 9.7¢ = $29.10 → $29      1,000cr → $97      3,000cr → $291
 *
 * ROUNDING IS LOAD-BEARING (PANDA-1414): the prior model rounded UP, which
 * prices Pro's 300-credit base at $30 instead of the approved $29. It must also
 * be round-HALF-UP — 7,500cr = $727.50 must bill $728. `Math.round` is
 * half-up; a banker's-rounding / toFixed-based helper would yield $727. There is
 * a regression test pinning exactly this (tests/unit/lib/plans-pricing.test.ts).
 *
 * The tier multiplier is ×1 for every plan today (tiers differ by INCLUDED
 * credits, not rate); it is applied last so a future per-tier rate still works.
 */
export function tierMonthlyCents(tier: PaidTierId, credits: number): number {
  const dollarCents =
    Math.round((credits * BASE_RATE_CENTS_PER_CREDIT) / 100) * 100;
  return dollarCents * TIER_MULTIPLIER[tier];
}

/** Annual price (per YEAR) = monthly × 10 (two months free). */
export function tierAnnualCents(tier: PaidTierId, credits: number): number {
  return tierMonthlyCents(tier, credits) * ANNUAL_MONTHS_CHARGED;
}

/** Per-month figure shown on an annual card (annual ÷ 12), whole cents. */
export function tierAnnualPerMonthCents(
  tier: PaidTierId,
  credits: number,
): number {
  return Math.round(tierAnnualCents(tier, credits) / 12);
}

/** Price for the active cadence, as the card presents it (per-month cents). */
export function tierPerMonthCents(
  tier: PaidTierId,
  credits: number,
  cadence: BillingCadence,
): number {
  return cadence === "annual"
    ? tierAnnualPerMonthCents(tier, credits)
    : tierMonthlyCents(tier, credits);
}

/** Whole-dollar string for display, e.g. 14300 → "$143". */
export function centsToDollars(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString("en-US")}`;
}

/**
 * Validates a dropdown selection server-side. PER-PLAN (PANDA-1414): a credit
 * total is only valid if it appears on THAT tier's ladder, which means a
 * subscriber can never buy fewer credits than their plan's base (e.g. Scale
 * cannot select 300). Previously a single shared ladder let any plan pick 650.
 */
export function isValidLadderCredits(
  tier: PaidTierId,
  credits: number,
): boolean {
  return PLAN_CREDIT_LADDER[tier].includes(credits);
}

/** The credits a tier's dropdown opens on: its base (cheapest, always valid). */
export function defaultLadderCredits(tier: PaidTierId): number {
  return PLAN_BASE_CREDITS[tier];
}

// ── tier display content (ticket "Content notes") ───────────────────────────
// Cards render from here. Feature lists are cumulative in spirit (Grow builds on
// Pro, Scale on Grow) per the ticket. Free is fixed-price; paid tiers carry the
// credit dropdown.

export interface PricingTier {
  id: PricingTierId;
  name: string;
  tagline: string;
  /** Paid tiers use the credit dropdown; free is a flat $0. */
  hasCreditDropdown: boolean;
  /** Multiplier (paid only) — null for free. */
  multiplier: number | null;
  /** Whether the price is approved to charge (paid only). */
  ratesConfirmed: boolean;
  highlighted: boolean;
  /** Primary CTA copy (mirrors the mockup's per-card buttons). */
  cta: string;
  features: string[];
}

export const PRICING_TIERS: Record<PricingTierId, PricingTier> = {
  free: {
    id: "free",
    name: "Free",
    tagline: "Try Panda. Build your first app today.",
    hasCreditDropdown: false,
    multiplier: null,
    ratesConfirmed: true,
    highlighted: false,
    cta: "Start free",
    features: [
      "300 starter credits (up to 50/day)",
      "3 projects",
      "Business-ready templates",
      "Panda subdomain",
      "Cloud hosting included",
      // The backend boundary, stated on the card (PANDA-1676/1695). A Free app
      // NEVER gets a real database — appGetsProductionDatabase (lib/turso/
      // provision.ts) blocks provisioning, so login + saved data are built as a
      // labeled demo that resets. Naming it here is the point: the card used to
      // list only payments/domain as exclusions, so a free user reasonably read
      // accounts + saved data as included.
      "Database & customer login as a demo (data resets)",
      // Free explicitly excludes payments + custom domain.
      "No payments or custom domain",
    ],
  },
  pro: {
    id: "pro",
    name: "Pro",
    tagline: "Build & launch under your own brand.",
    hasCreditDropdown: true,
    multiplier: TIER_MULTIPLIER.pro,
    ratesConfirmed: RATES_CONFIRMED.pro,
    highlighted: true, // "Most popular"
    cta: "Launch my business",
    features: [
      // Build-quality benefit, led (survives purchase-confirmation's first-5
      // slice). Wording is the canonical PREMIUM_BUILD_MODEL_COPY.feature from
      // lib/ai/model-quality-tier.ts — kept inline (not imported) to avoid a
      // credit-tiers → model-quality-tier → plans → credit-tiers import cycle.
      // If you change one, change both.
      "Stronger AI model — more reliable on complex, multi-step apps",
      "Unlimited projects",
      // The paid unlock the Free card's demo line points at: a real provisioned
      // database + real sign-in (PlanDef.persistentData / customerAccounts).
      // Listed second so it survives purchase-confirmation's first-5 slice.
      "Real database & customer logins",
      "Custom domain & accept payments",
      "Remove Panda branding",
      "Hosting included for free", // Aaron's note
      "Premium templates",
    ],
  },
  grow: {
    id: "grow",
    name: "Grow",
    tagline: "Get customers on autopilot.",
    hasCreditDropdown: true,
    multiplier: TIER_MULTIPLIER.grow,
    ratesConfirmed: RATES_CONFIRMED.grow,
    highlighted: false,
    cta: "Start growing",
    features: [
      "Everything in Pro",
      "AI social posts",
      "WhatsApp + email messages",
      "Meta ad copy, briefs + static & video creatives",
      "Customer targeting + next steps",
    ],
  },
  scale: {
    id: "scale",
    name: "Scale",
    tagline: "Run a team of AI businesses.",
    hasCreditDropdown: true,
    multiplier: TIER_MULTIPLIER.scale,
    ratesConfirmed: RATES_CONFIRMED.scale,
    highlighted: false,
    cta: "Scale up",
    features: [
      "Everything in Grow",
      "Team workspaces with roles & permissions",
      "Internal publishing + personal projects",
      "Volume-based credit discounts",
      "SSO, SCIM & audit logs",
      "Dedicated support & onboarding",
    ],
  },
};
